import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiEdit2, FiTrash2, FiSearch } from 'react-icons/fi';
import api from '../../api';
import { extractError } from '../../api/client';
import { toast, confirmDelete, showError } from '../../utils/alert';
import PageHeader from '../../components/common/PageHeader';
import DataTable from '../../components/common/DataTable';
import BulkActions from '../../components/common/BulkActions';
import Pagination from '../../components/common/Pagination';
import StatusBadge from '../../components/common/StatusBadge';
import { useAuth } from '../../context/AuthContext';

export default function Parties() {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [params, setParams] = useState({ page: 1, search: '', per_page: 100 });
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.parties.list(params);
      const raw = Array.isArray(data.data) ? data.data : data.parties || data;
      const list = Array.isArray(raw) ? raw : raw && Array.isArray(raw.data) ? raw.data : [];
      setRows(list);
      setMeta(raw && !Array.isArray(raw)
        ? { current_page: raw.current_page, last_page: raw.last_page, total: raw.total, per_page: raw.per_page, per_page_options: data.per_page_options || raw.per_page_options }
        : null);
    } catch (err) {
      showError('Failed to load parties', extractError(err));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (row) => {
    const res = await confirmDelete(`party "${row.name}"`);
    if (!res.isConfirmed) return;
    try {
      await api.parties.remove(row.id);
      toast('success', '', 'Party deleted');
      load();
    } catch (err) {
      showError('Failed to delete party', extractError(err));
    }
  };

  return (
    <div className="page">
      <PageHeader
        title="Parties"
        subtitle="Manage customers and suppliers."
        crumbs={[{ label: 'Finance' }, { label: 'Parties' }]}
        actions={hasPermission('create-parties') && (
          <button className="btn btn-primary" onClick={() => navigate('/parties/new')}><FiPlus /> Add Party</button>
        )}
      />

      <div className="card">
        <div className="card-body">
          <div className="filters-bar">
            <div className="topbar-search">
              <FiSearch className="search-icon" />
              <input
                placeholder="Search parties..."
                value={params.search}
                onChange={(e) => setParams({ ...params, search: e.target.value, page: 1 })}
              />
            </div>
          </div>

          <BulkActions selectedIds={selectedIds} onClear={() => setSelectedIds([])} onDone={load} deleteType="parties" label="parties" />
          <DataTable
            loading={loading}
            data={rows}
            emptyTitle="No parties found"
            selectable
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            columns={[
              { key: 'name', label: 'Party', render: (r) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div className="user-avatar" style={{ width: 32, height: 32, fontSize: 13, borderRadius: 8 }}>
                    {r.name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontWeight: 600 }}>{r.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{r.phone || r.email || '-'}</div>
                  </div>
                </div>
              ) },
              { key: 'email', label: 'Email' },
              { key: 'gst_no', label: 'GST No.', render: (r) => r.gst_no || '-' },
              { key: 'ntn_no', label: 'NTN No.', render: (r) => r.ntn_no || '-' },
              { key: 'cnic', label: 'CNIC', render: (r) => r.cnic || '-' },
              { key: 'account', label: 'Linked Account', render: (r) => r.account ? r.account.name : <span className="badge badge-yellow">No account</span> },
              { key: 'is_active', label: 'Status', render: (r) => <StatusBadge status={r.is_active ? 'active' : 'inactive'} /> },
              { key: 'id', label: '', align: 'right', render: (r) => (
                <div className="actions">
                  {hasPermission('update-parties') && <button className="btn-icon" onClick={() => navigate(`/parties/${r.id}/edit`)}><FiEdit2 /></button>}
                  {hasPermission('delete-parties') && <button className="btn-icon danger" onClick={() => handleDelete(r)}><FiTrash2 /></button>}
                </div>
              ) },
            ]}
          />
          {meta && <Pagination meta={meta} onPage={(p) => setParams({ ...params, page: p })} onPerPage={(n) => setParams({ ...params, per_page: n, page: 1 })} />}
        </div>
      </div>
    </div>
  );
}
